import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DailySessionStat } from "@/types/admin.types";
import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface DailySessionsChartProps {
  data: DailySessionStat[];
}

export function DailySessionsChart({ data }: DailySessionsChartProps) {
  const aggregatedData = useMemo(() => {
    const dayCounts = {
      Sun: 0,
      Mon: 0,
      Tue: 0,
      Wed: 0,
      Thu: 0,
      Fri: 0,
      Sat: 0,
    };

    data.forEach((item) => {
      // Ensure date parsing handles timezone correctly or treat as UTC string if needed
      // Assuming item.date is YYYY-MM-DD
      const date = new Date(item.date);
      // Use UTC methods to avoid timezone shifts if the date string is just a date
      // But new Date("2025-12-09") is treated as UTC in some environments or local in others depending on format
      // To be safe with YYYY-MM-DD, it's often treated as UTC.
      // Let's use getUTCDay() if the input is YYYY-MM-DD to avoid shifting to previous day
      const dayIndex = new Date(item.date + "T12:00:00").getDay();
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const dayName = days[dayIndex] as keyof typeof dayCounts;

      dayCounts[dayName] += item.count;
    });

    // Order starting from Monday
    const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return order.map((day) => ({
      day,
      count: dayCounts[day as keyof typeof dayCounts],
    }));
  }, [data]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">
          Sessions by Day of Week
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={aggregatedData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "transparent" }}
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                }}
              />
              <Bar dataKey="count" fill="#1e40af" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
